import React from 'react';

import { Dialog, Slide } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import HowToRegOutlinedIcon from '@material-ui/icons/HowToRegOutlined';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';

import { normalizeAgents } from 'components/AgentLanding/AgentLandingPrimitives';

const BiographyTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

BiographyTransition.displayName = 'BiographyTransition';

const BiographyModal = ({ open, onClose, agentData }) => {
  const agents = normalizeAgents(agentData);
  const agentNames = agents
    .map((agent) => `${agent.firstName} ${agent.lastName || ''}`.trim())
    .join(agents.length > 1 ? ' & ' : '');
  const isTeam = agents.length > 1;
  const biography = agentData?.agentLandingBio
    || `${isTeam ? 'We' : 'I'} provide local real estate guidance with an emphasis on clear market context, thoughtful strategy, and responsive service.`;

  return (
    <Dialog
      aria-labelledby="al-bio-modal-title"
      aria-describedby="al-bio-modal-description"
      BackdropProps={{ className: 'al-bio-modal-backdrop' }}
      classes={{ root: 'al-bio-dialog', paper: 'al-bio-modal-paper' }}
      fullWidth
      keepMounted
      maxWidth="md"
      open={open}
      onClose={onClose}
      scroll="paper"
      TransitionComponent={BiographyTransition}
      transitionDuration={{ enter: 360, exit: 220 }}
    >
      <button className="al-bio-modal-close" type="button" aria-label="Close biography" onClick={onClose}>
        <CloseRoundedIcon aria-hidden="true" />
      </button>
      <div className="al-bio-modal-grid">
        <aside className="al-bio-modal-profile">
          <div className={`al-bio-modal-portraits${agents.length > 1 ? ' al-bio-modal-portraits--team' : ''}`}>
            {agents.map((agent) => (
              agent.pictureUrl ? (
                <img
                  key={`${agent.firstName}-${agent.lastName}`}
                  src={agent.pictureUrl}
                  alt={`Portrait of ${agent.firstName} ${agent.lastName || ''}`.trim()}
                />
              ) : (
                <span className="al-bio-modal-fallback" key={`${agent.firstName}-${agent.lastName}`} aria-hidden="true">
                  {agent.firstName.charAt(0)}
                  {agent.lastName?.charAt(0)}
                </span>
              )
            ))}
          </div>
          <span className="al-bio-modal-kicker">
            Your local
            {' '}
            {agents.length > 1 ? 'advisors' : 'advisor'}
          </span>
          <h2 id="al-bio-modal-title">{agentNames}</h2>
          <p>
            {isTeam ? 'We offer' : 'I offer'}
            {' '}
            personal guidance, grounded in the local market.
          </p>
        </aside>
        <section className="al-bio-modal-content">
          <span className="al-bio-modal-overline">A thoughtful approach to real estate</span>
          <h3>Local experience. Clear guidance.</h3>
          <div id="al-bio-modal-description" className="al-bio-modal-copy">{biography}</div>
          <div className="al-bio-modal-contacts">
            {agents.map((agent) => (
              <div className="al-bio-modal-contact" key={`${agent.firstName}-${agent.lastName}`}>
                {agents.length > 1 && <strong>{agent.firstName}</strong>}
                {agent.phoneNumber && (
                  <a href={`tel:${agent.phoneNumber}`}>
                    <PhoneOutlinedIcon aria-hidden="true" />
                    {agent.phoneNumber}
                  </a>
                )}
                {agent.email && (
                  <a href={`mailto:${agent.email}`}>
                    <EmailOutlinedIcon aria-hidden="true" />
                    {agent.email}
                  </a>
                )}
                {agent.website && (
                  <a href={agent.website} target="_blank" rel="noreferrer">
                    <LanguageOutlinedIcon aria-hidden="true" />
                    {agent.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {agent.licenseNumber && (
                  <span className="al-bio-modal-license">
                    <HowToRegOutlinedIcon aria-hidden="true" />
                    <span>
                      DRE #
                      {agent.licenseNumber}
                    </span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </Dialog>
  );
};

export default BiographyModal;
